import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { storage, db, auth } from '../../config/firebase';

const safeName = (name = 'file') => String(name || 'file').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-140) || 'file';
const safeSegment = (value = '') => String(value || '').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 120);
const safeFolder = (value = 'uploads') => String(value || 'uploads').split('/').map(safeSegment).filter(Boolean).join('/') || 'uploads';

export const UPLOAD_LIMITS = Object.freeze({
  image: 5 * 1024 * 1024,
  video: 50 * 1024 * 1024,
  audio: 20 * 1024 * 1024,
  document: 15 * 1024 * 1024,
  default: 15 * 1024 * 1024,
});

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime']);
const AUDIO_TYPES = new Set(['audio/webm', 'audio/mpeg', 'audio/mp4', 'audio/ogg', 'audio/wav']);
const DOCUMENT_TYPES = new Set([
  'application/pdf', 'text/plain', 'text/csv', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
]);
const GENERAL_TYPES = new Set([...IMAGE_TYPES, ...VIDEO_TYPES, ...AUDIO_TYPES, ...DOCUMENT_TYPES]);

export const validateUploadFile = (file, { kind = 'default', maxBytes, allowedTypes } = {}) => {
  if (!file) throw new Error('A file is required.');
  const type = String(file.type || '').toLowerCase();
  const size = Number(file.size || 0);
  const typeSet = allowedTypes
    ? new Set(allowedTypes.map((value) => String(value).toLowerCase()))
    : kind === 'image' ? IMAGE_TYPES
      : kind === 'video' ? VIDEO_TYPES
        : kind === 'audio' ? AUDIO_TYPES
          : kind === 'document' ? DOCUMENT_TYPES
            : GENERAL_TYPES;
  const limit = Number(maxBytes || UPLOAD_LIMITS[kind] || UPLOAD_LIMITS.default);
  if (!typeSet.has(type)) {
    const label = kind === 'image' ? 'PNG, JPG/JPEG or WEBP'
      : kind === 'video' ? 'MP4, WEBM or MOV'
        : kind === 'audio' ? 'WEBM, MP3, MP4, OGG or WAV'
          : kind === 'document' ? 'PDF, Office, text/CSV or ZIP' : 'a supported image, video, audio or document file';
    throw new Error(`Unsupported ${kind} file. Please choose ${label}.`);
  }
  if (size <= 0) throw new Error('The selected file is empty.');
  if (size > limit) throw new Error(`File is too large. Maximum size is ${Math.round(limit / (1024 * 1024))} MB.`);
  return true;
};

const ownerMatches = (row = {}, firebaseUser) => {
  if (!firebaseUser?.uid) return false;
  const ids = [row.adminId, row.ownerId, row.createdBy, row.userId, row.uid].filter(Boolean).map(String);
  const emails = [row.adminEmail, row.ownerEmail, row.email].filter(Boolean).map((value) => String(value).toLowerCase());
  if (ids.includes(String(firebaseUser.uid))) return true;
  return Boolean(firebaseUser.email && emails.includes(String(firebaseUser.email).toLowerCase()));
};

/** Resolve the signed-in user's verified company without trusting a page prop. */
export async function resolveAuthenticatedCompanyId(preferredCompanyId = null) {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser?.uid) throw new Error('Please sign in before uploading company files.');
  const preferred = typeof preferredCompanyId === 'string' && preferredCompanyId.trim() ? preferredCompanyId.trim() : null;

  let profile = null;
  try {
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (snap.exists()) profile = snap.data() || {};
  } catch (error) {
    console.warn('Upload company profile lookup skipped:', error?.message || error);
  }

  const profileCompanyId = [profile?.companyId, profile?.company?.companyId, profile?.company?.id]
    .find((value) => typeof value === 'string' && value.trim())?.trim() || null;
  if (preferred && profileCompanyId && preferred !== profileCompanyId) throw new Error('The selected company does not match your authenticated company.');
  if (profileCompanyId) return profileCompanyId;

  try {
    const direct = await getDoc(doc(db, 'companies', firebaseUser.uid));
    if (direct.exists()) {
      const row = direct.data() || {};
      const hasExplicitOwner = Boolean(row.adminId || row.ownerId || row.createdBy || row.userId || row.uid || row.adminEmail || row.ownerEmail);
      if ((!hasExplicitOwner || ownerMatches(row, firebaseUser)) && (!preferred || preferred === direct.id)) return direct.id;
    }
  } catch (error) {
    console.warn('Legacy upload company lookup skipped:', error?.message || error);
  }

  const lookups = [['adminId', firebaseUser.uid], ['ownerId', firebaseUser.uid]];
  if (firebaseUser.email) lookups.push(['adminEmail', firebaseUser.email], ['ownerEmail', firebaseUser.email]);
  for (const [field, value] of lookups) {
    try {
      const snap = await getDocs(query(collection(db, 'companies'), where(field, '==', value)));
      if (!snap.empty) {
        const id = snap.docs[0].id;
        if (preferred && preferred !== id) throw new Error('The selected company does not match your authenticated company.');
        return id;
      }
    } catch (error) {
      if (/does not match/.test(String(error?.message || ''))) throw error;
      console.warn(`Upload company lookup by ${field} skipped:`, error?.message || error);
    }
  }
  throw new Error(preferred
    ? 'Your company membership could not be verified. Refresh the page or sign in again.'
    : 'No company is linked to this account. Complete company setup before uploading company files.');
}

/**
 * Storage is platform-owned. Company Portal reads only the public runtime mirror
 * written by Super Admin. It never reads systemConfig/private integration data.
 */
export async function getStorageProvider(companyId = null) {
  const fallback = { provider: 'firebase', firebase: { basePath: 'companies' }, cloudinary: { cloudName: '', uploadPreset: '', folder: 'telcofair' } };

  try {
    const snap = await getDoc(doc(db, 'systemSettings', 'public_runtime'));
    const runtime = snap.exists() ? snap.data() || {} : {};
    const cfg = runtime?.storage && typeof runtime.storage === 'object' ? runtime.storage : {};
    const provider = ['firebase', 'cloudinary'].includes(String(cfg.provider || '').toLowerCase()) ? String(cfg.provider).toLowerCase() : 'firebase';
    return {
      ...fallback,
      ...cfg,
      provider,
      firebase: { ...fallback.firebase, ...(cfg.firebase || {}) },
      cloudinary: { ...fallback.cloudinary, ...(cfg.cloudinary || {}) },
    };
  } catch (error) {
    console.warn('Platform storage runtime unavailable; Firebase Storage fallback is active:', error?.message || error);
    return fallback;
  }
}

const buildScope = async ({ scope = 'company', companyId, userId, ownerId }) => {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser?.uid) throw new Error('Please sign in before uploading files.');
  if (scope === 'user') {
    const uid = safeSegment(ownerId || userId || firebaseUser.uid);
    if (!uid || uid !== safeSegment(firebaseUser.uid)) throw new Error('You can only upload files to your own user profile.');
    let verifiedCompanyId = null;
    try { verifiedCompanyId = await resolveAuthenticatedCompanyId(companyId || null); } catch (_) { /* registration may precede company selection */ }
    return { scope: 'user', ownerId: uid, userId: uid, companyId: verifiedCompanyId, firebaseBasePath: 'users', firebaseOwner: uid };
  }
  if (scope === 'company') {
    const verifiedCompanyId = await resolveAuthenticatedCompanyId(companyId || null);
    return { scope: 'company', ownerId: verifiedCompanyId, companyId: verifiedCompanyId, firebaseBasePath: 'companies', firebaseOwner: verifiedCompanyId };
  }
  throw new Error('Unsupported upload scope.');
};

export async function uploadWithFirebase({ companyId, ownerId, scope = 'company', file, folder = 'uploads', onProgress, basePath }) {
  const root = safeSegment(basePath || (scope === 'user' ? 'users' : 'companies')) || (scope === 'user' ? 'users' : 'companies');
  const owner = safeSegment(ownerId || companyId);
  if (!owner) throw new Error('Upload owner is missing.');
  const path = `${root}/${owner}/${safeFolder(folder)}/${Date.now()}_${safeName(file.name)}`;
  const fileRef = ref(storage, path);
  const metadata = { ownerId: owner, scope, uploadedBy: auth.currentUser?.uid || owner };
  if (companyId) metadata.companyId = String(companyId);
  const task = uploadBytesResumable(fileRef, file, { contentType: file.type || undefined, customMetadata: metadata });
  await new Promise((resolve, reject) => {
    task.on('state_changed', (snapshot) => {
      onProgress?.(Math.round((snapshot.bytesTransferred / Math.max(snapshot.totalBytes, 1)) * 100));
    }, reject, resolve);
  });
  return { provider: 'firebase', scope, ownerId: owner, companyId: companyId || null, url: await getDownloadURL(task.snapshot.ref), path, fullPath: task.snapshot.ref.fullPath, contentType: file.type || '', bytes: file.size || 0 };
}

/**
 * Cloudinary uses the unsigned upload preset configured by Super Admin.
 * Only Cloud Name, unsigned Upload Preset and folder are exposed through the
 * browser-safe runtime document. No API secret or delete token is requested.
 */
export async function uploadWithCloudinary({ companyId, ownerId, userId, scope = 'company', file, folder = 'uploads', onProgress, publicId }) {
  const storageSettings = await getStorageProvider(companyId);
  const cfg = storageSettings?.cloudinary || {};
  const cloudName = String(cfg.cloudName || '').trim();
  const uploadPreset = String(cfg.uploadPreset || '').trim();
  const baseFolder = safeFolder(cfg.folder || 'telcofair');
  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary is not configured. Set Cloud Name and an unsigned Upload Preset in Super Admin Settings.');
  }

  const owner = safeSegment(ownerId || userId || companyId || auth.currentUser?.uid || '');
  const scopedFolder = [baseFolder, scope === 'user' ? 'users' : 'companies', owner, safeFolder(folder)]
    .filter(Boolean).join('/');

  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', uploadPreset);
  form.append('folder', scopedFolder);
  if (publicId) form.append('public_id', safeSegment(publicId));

  const xhr = new XMLHttpRequest();
  const result = await new Promise((resolve, reject) => {
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudName)}/auto/upload`);
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress?.(Math.round((event.loaded / event.total) * 100));
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data?.error?.message || 'Cloudinary upload failed. Check Cloud Name and unsigned Upload Preset.'));
      } catch (error) { reject(error); }
    };
    xhr.onerror = () => reject(new Error('Cloudinary upload network error.'));
    xhr.send(form);
  });
  return {
    provider: 'cloudinary', scope, ownerId: ownerId || userId || companyId, companyId: companyId || null,
    url: result.secure_url, publicId: result.public_id, resourceType: result.resource_type,
    width: result.width, height: result.height, bytes: result.bytes, format: result.format,
  };
}

export async function uploadFile({ companyId, userId, ownerId, scope = 'company', file, folder = 'uploads', onProgress, publicId, kind = 'default', maxBytes, allowedTypes }) {
  validateUploadFile(file, { kind, maxBytes, allowedTypes });
  const context = await buildScope({ scope, companyId, userId, ownerId });
  const storageSettings = await getStorageProvider(context.companyId);
  const active = storageSettings.provider || 'firebase';
  if (active === 'cloudinary') return uploadWithCloudinary({ ...context, file, folder, onProgress, publicId });
  return uploadWithFirebase({ ...context, file, folder, onProgress, basePath: context.scope === 'user' ? 'users' : 'companies' });
}

export async function deleteFile({ companyId, userId, ownerId, scope = 'company', asset }) {
  if (!asset) return { skipped: true };
  const context = await buildScope({ scope, companyId, userId, ownerId });
  if (asset.provider === 'cloudinary' && asset.publicId) {
    // Unsigned Cloudinary uploads intentionally expose no API secret/delete token.
    // Keep the stored asset reference replaceable without issuing an invalid
    // browser-side delete request. Server-side cleanup can be added only when
    // Cloudinary API Key/Secret are configured in protected backend settings.
    return { success: true, skippedRemoteDelete: true, provider: 'cloudinary', publicId: asset.publicId };
  }
  const path = String(asset.fullPath || asset.path || '');
  if (!path) return { skipped: true };
  const settings = await getStorageProvider(context.companyId);
  const expectedRoot = context.scope === 'user'
    ? `users/${context.ownerId}/`
    : `companies/${context.companyId}/`;
  if (!path.startsWith(expectedRoot)) throw new Error('Refusing to delete a file outside the authenticated upload scope.');
  await deleteObject(ref(storage, path));
  return { success: true };
}

export default { uploadFile, deleteFile, getStorageProvider, resolveAuthenticatedCompanyId, validateUploadFile, uploadWithFirebase, uploadWithCloudinary };
